import {
  AssetType,
  TransformationVariant,
  DeviceFamily,
  Placement,
} from '@amzn/fire-tv-campaign-automation-service';
import { createContext } from 'react';
import { getVariantsFromConfig } from 'src/config/templateVariantConfiguration';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';
import { ProvisionedConstants, STAGE } from 'src/constants';
import { getFirstValueIfSingleItem } from 'src/helpers';
import { AssetState, AssetStatus, InputType } from 'src/types';

/**
 * Represents the state of all assets in the campaign automation form.
 * Maps each asset type to its current state.
 * @example
 * {
 *   [AssetType.BACKGROUND_IMAGE]: { status: AssetStatus.PENDING_INPUT },
 *   [AssetType.LOGO]: { status: AssetStatus.COMPLETED, key: '/logo_images/logo_filename.png' }
 *   [AssetType.VIDEO_TRAILER]: { status: AssetStatus.FAILED, error: 'Invalid video duration' }
 * }
 */
export type AssetContextState = Record<AssetType, AssetState>;

/**
 * Interface defining the shape of the Asset context value.
 * Provides access to asset states and methods to update them.
 */
export interface AssetContextValue {
  /**
   * Current state of all assets in the form
   */
  assetStates: AssetContextState;

  /**
   * Updates the state of a specific asset
   * @param assetType - The type of asset to update (e.g., BACKGROUND_IMAGE, LOGO)
   * @param state - Partial state to merge with the existing asset state
   */
  updateAssetState: (assetType: AssetType, state: Partial<AssetState>) => void;
}

/**
 * Default state for all asset types.
 * Each asset starts in PENDING_INPUT status.
 */
export const DEFAULT_ASSET_STATE: AssetContextState = {
  [AssetType.BACKGROUND_IMAGE]: {
    status: AssetStatus.PENDING_INPUT,
    transformationVariant: TransformationVariant.NO_TRANSFORMATION,
    inputType: InputType.SEARCH,
  },
  [AssetType.LOGO]: {
    status: AssetStatus.PENDING_INPUT,
    transformationVariant: TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
    inputType: InputType.LOGO_TEXT,
  },
  [AssetType.VIDEO_TRAILER]: {
    status: AssetStatus.PENDING_INPUT,
    transformationVariant: TransformationVariant.NO_TRANSFORMATION,
    inputType: InputType.UPLOAD,
  },
} as const;

/**
 * Returns device-family-aware default asset state.
 * Logo variant is determined by device family configuration.
 */
export const getDefaultAssetState = (
  deviceFamily: DeviceFamily,
  placement: Placement,
): AssetContextState => {
  const logoVariants = getVariantsFromConfig(
    ProvisionedConstants.STAGE as STAGE,
    deviceFamily,
    placement,
  )?.[AssetType.LOGO];

  const logoVariant =
    (logoVariants?.[0]?.value as TransformationVariant) || TransformationVariant.NO_TRANSFORMATION;

  return {
    [AssetType.BACKGROUND_IMAGE]: {
      status: AssetStatus.PENDING_INPUT,
      transformationVariant: TransformationVariant.NO_TRANSFORMATION,
      inputType: InputType.SEARCH,
    },
    [AssetType.LOGO]: {
      status: AssetStatus.PENDING_INPUT,
      transformationVariant: logoVariant,
      inputType: InputType.LOGO_TEXT,
    },
    [AssetType.VIDEO_TRAILER]: {
      status: AssetStatus.PENDING_INPUT,
      transformationVariant: TransformationVariant.NO_TRANSFORMATION,
      inputType: InputType.UPLOAD,
    },
  };
};

/**
 * React Context for managing campaign asset states.
 *
 * @example
 * // Using the context in a component
 * const { assetStates, updateAssetState } = useContext(AssetContext);
 *
 * // Check if background image is uploaded
 * const isBackgroundUploaded = assetStates[AssetType.BACKGROUND_IMAGE].status === AssetStatus.COMPLETED;
 *
 * // Update an asset state
 * updateAssetState(AssetType.LOGO, { status: AssetStatus.PENDING_UPLOAD });
 */
export const AssetContext = createContext<AssetContextValue>({
  assetStates: DEFAULT_ASSET_STATE,
  updateAssetState: () => {},
});
